import React from "react";
import { humanStackLabel } from "@/lib/stackLabel";

// Sprint 62.1 — Portfolio cockpit strip: stack-shape breakdown + expected-
// duplicate histogram (from the field-relative EV ranker) + stack/own summary.
const DUP_BUCKETS = [
  { key: "lt001", label: "< .01", test: (d) => d < 0.01, tone: "bg-[#39FF14]" },
  { key: "001_005", label: ".01–.05", test: (d) => d >= 0.01 && d < 0.05, tone: "bg-[#39FF14]/70" },
  { key: "005_02", label: ".05–.2", test: (d) => d >= 0.05 && d < 0.2, tone: "bg-[#FFCC00]" },
  { key: "02_1", label: ".2–1", test: (d) => d >= 0.2 && d < 1, tone: "bg-[#FF8A00]" },
  { key: "gte1", label: "1+", test: (d) => d >= 1, tone: "bg-[#FF3B30]" },
];

export default function PortfolioHealth({ lineups }) {
  const rows = Array.isArray(lineups) ? lineups : [];
  if (!rows.length) return null;
  const n = rows.length;

  const stackCounts = {};
  for (const L of rows) {
    const k = humanStackLabel(L) || L.stack_type || "—";
    stackCounts[k] = (stackCounts[k] || 0) + 1;
  }
  const stacks = Object.entries(stackCounts).sort((a, b) => b[1] - a[1]);

  const dupes = rows.map((L) => Number(L.expected_dupes)).filter((d) => Number.isFinite(d));
  const buckets = DUP_BUCKETS.map((b) => ({ ...b, count: dupes.filter(b.test).length }));
  const maxBucket = Math.max(1, ...buckets.map((b) => b.count));
  const avgDupes = dupes.length ? dupes.reduce((s, d) => s + d, 0) / dupes.length : null;
  const uniqueShare = dupes.length ? (dupes.filter((d) => d < 0.05).length / dupes.length) * 100 : null;
  const avgOwn = rows.reduce((s, L) => s + Number(L.total_own || 0), 0) / n;
  const avgProj = rows.reduce((s, L) => s + Number(L.projection || 0), 0) / n;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="portfolio-health">
      <div className="bg-[#121212] border border-zinc-800 p-3">
        <div className="overline mb-2">Stack Shapes</div>
        <div className="space-y-1.5" data-testid="portfolio-stack-breakdown">
          {stacks.map(([k, v]) => (
            <div key={k} className="text-[11px] mono">
              <div className="flex justify-between text-zinc-300">
                <span className="truncate">{k}</span>
                <span className="text-[#39FF14] font-bold shrink-0 ml-2">{v} · {((v / n) * 100).toFixed(0)}%</span>
              </div>
              <div className="h-1 bg-zinc-900 mt-0.5">
                <div className="h-full bg-[#39FF14]/70" style={{ width: `${(v / n) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#121212] border border-zinc-800 p-3">
        <div className="overline mb-2" title="Expected number of other entries in the field holding this exact lineup (field size × Π ownership). Lower is better — < .05 means you're effectively 1-of-1.">
          Dup Risk · expected copies
        </div>
        {dupes.length === 0 ? (
          <div className="text-[11px] text-zinc-500 mono" data-testid="portfolio-dup-empty">
            Dup risk is computed for MILLY / GPP builds after Field-EV ranking.
          </div>
        ) : (
          <div className="flex items-end gap-1.5 h-20" data-testid="portfolio-dup-histogram">
            {buckets.map((b) => (
              <div key={b.key} className="flex-1 flex flex-col items-center justify-end h-full" title={`${b.count} lineup${b.count === 1 ? "" : "s"} with ${b.label} expected copies`}>
                <div className="text-[10px] mono text-zinc-300 mb-0.5">{b.count}</div>
                <div className={`w-full ${b.tone}`} style={{ height: `${Math.max(2, (b.count / maxBucket) * 56)}px` }} data-testid={`dup-bucket-${b.key}`} />
                <div className="text-[8px] mono text-zinc-500 mt-1 uppercase">{b.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#121212] border border-zinc-800 p-3 space-y-2">
        <div className="overline">Portfolio</div>
        <Row label="Lineups" value={n} />
        <Row label="Avg Proj" value={avgProj.toFixed(1)} />
        <Row label="Avg Own Σ" value={`${avgOwn.toFixed(1)}%`} tone={avgOwn > 100 ? "text-[#FF3B30]" : avgOwn > 90 ? "text-[#FFCC00]" : "text-[#39FF14]"} />
        {avgDupes != null && <Row label="Avg Copies" value={avgDupes.toFixed(3)} tone={avgDupes > 0.2 ? "text-[#FF3B30]" : avgDupes > 0.05 ? "text-[#FFCC00]" : "text-[#39FF14]"} testId="portfolio-avg-dupes" />}
        {uniqueShare != null && <Row label="1-of-1 share" value={`${uniqueShare.toFixed(0)}%`} testId="portfolio-unique-share" />}
      </div>
    </div>
  );
}

function Row({ label, value, tone = "text-[#39FF14]", testId }) {
  return (
    <div className="flex justify-between items-center text-[11px] mono">
      <span className="text-zinc-400 uppercase tracking-widest text-[9px]">{label}</span>
      <span className={`font-bold ${tone}`} data-testid={testId}>{value}</span>
    </div>
  );
}
